import {
    Badge,
    BookmarkButton,
    CategoryBadge,
    dateCountDown,
    ImageOrPlaceholder,
    useTranslations,
} from 'react-ui';
import { BalloonEventModel, getFrontendUrl } from 'ts-core';
import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { formatDate } from '../utils/date';
import { trackMixpanel } from '../utils/mixpanel';
import { useEventInteraction } from '../hooks/useEventInteraction';
import {CategoryBadges} from "./CategoryBadges.tsx";
import {DateBadge} from "./DateBadge.tsx";
import {CountdownBadge} from "./CountdownBadge.tsx";
import {useEventClick} from "../hooks/useEventClick.tsx";

export interface EventItemProps {
    event: BalloonEventModel;
    selected: boolean;
    hasSelectedEvent: boolean;
}

function getWebPlatformLink(event: BalloonEventModel) {
    const frontendBaseUrl = getFrontendUrl();
    return `${frontendBaseUrl}/event/${event.slug}`;
}

export const EventItem: React.FC<EventItemProps> = memo(function EventItem({
    event,
    selected,
    hasSelectedEvent,
}) {
    const { hovered, onMouseEnter, onMouseLeave, onClick, eventRef } = useEventInteraction(event);
    const { openDetailsOnWebPlatform } = useBalloonEventsOptions();
    const { translated, language } = useTranslations();
    const {handleClick} = useEventClick(event, onClick, 'list')
    const highlighted = hovered || selected;
    const shouldDim = !highlighted && hasSelectedEvent;

    const ItemContent = (
        <div
            ref={eventRef}
            className={`w-full h-37 flex gap-3 transition-all duration-300 ${
                selected ? 'ring-2 ring-white border-balloon border-[2px]' : ''
            } ${
                highlighted ? 'bg-gray-50 scale-110' : 'bg-transparent'
            } ${shouldDim ? 'opacity-30' : 'opacity-100'} rounded-[10px] p-2`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={handleClick}
        >
            {/* Thumbnail (left side) - clean, no overlays */}
            <div className="w-32 h-32 relative rounded-[10px] overflow-hidden flex-shrink-0">
                {/* Bookmark Button (top-right) */}
                <div className="bookmark-button-container absolute right-2 top-2 z-10">
                    <BookmarkButton event={event} trackingSource="event-item" />
                </div>
                <ImageOrPlaceholder
                    event={event}
                    size={32}
                    className="w-full h-full object-cover"
                    hideControls
                />
                {/* Countdown Badge */}
                <CountdownBadge event={event} className={"flex items-center justify-center drop-shadow-md absolute bottom-2 left-2"}/>
            </div>

            {/* Content Section (right side) */}
            <div className="flex-1 flex flex-col gap-1 min-w-0">
                {/* Title */}
                <h3 className="text-sm font-bold text-Text leading-tight line-clamp-2 font-roboto">
                    {event.title}
                </h3>

                {/* Location */}
                {event.locationName && (
                    <div className="flex items-center gap-1">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            className="w-2.5 h-3 flex-shrink-0 text-Text"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                            />
                        </svg>
                        <span className="text-[10px] text-Text line-clamp-1 font-roboto">
                            {event.locationName}
                        </span>
                    </div>
                )}
                <DateBadge event={event}/>
                <CategoryBadges categories={event.categories} className={"flex gap-1"} language={language} />
            </div>
        </div>
    );

    return ItemContent;
});
